import type { SchemaData } from '@lemonade-hq/chat-engine';
import type {
  WizardComponentAdapterFn,
  WizardComponentAdapterFunctionalComponent,
  WizardComponentsMap,
} from '@lemonade-hq/chat-wizard-client-base';
import { filterConditionalItems } from '@lemonade-hq/chat-wizard-client-base';
import type { WizardComponentBase } from '@lemonade-hq/chat-wizard-common-definitions';
import { wrap } from '@lemonade-hq/ts-helpers';
import isEqual from 'lodash/isEqual';
import type { RefObject } from 'react';
import { useMemo, useRef } from 'react';
import type { UseWizardAsyncComponents } from './useWizardAsyncComponents';
import { useWizardAsyncComponents } from './useWizardAsyncComponents';

const filterComponentsByProps = <
  TComponentType extends string = string,
  TComponent extends WizardComponentBase<TComponentType> = WizardComponentBase<TComponentType>,
>(
  loadedComponents: WizardComponentAdapterFunctionalComponent<TComponentType>[],
  filteredProps: TComponent[],
  allProps: TComponent[],
): WizardComponentAdapterFunctionalComponent<TComponentType>[] => {
  const matchedIndexes = filteredProps.map(prop => allProps.indexOf(prop));
  return loadedComponents.filter((_, index) => matchedIndexes.includes(index));
};

interface UseWizardComponentsProps<
  TComponentType extends string = string,
  TComponent extends WizardComponentBase<TComponentType> = WizardComponentBase<TComponentType>,
> {
  readonly componentsMap: WizardComponentsMap<TComponentType>;
  readonly presentationLock?: RefObject<boolean>;
  readonly currentStepComponent?: TComponent | TComponent[];
  readonly answers?: SchemaData;
}

interface UseWizardComponents<
  TComponentType extends string = string,
  TComponent extends WizardComponentBase<TComponentType> = WizardComponentBase<TComponentType>,
> extends UseWizardAsyncComponents<TComponentType> {
  readonly componentsProps: TComponent[];
}

export const useWizardComponents = <
  TComponentType extends string = string,
  TComponent extends WizardComponentBase<TComponentType> = WizardComponentBase<TComponentType>,
>({
  componentsMap,
  presentationLock = { current: false },
  currentStepComponent,
  answers,
}: UseWizardComponentsProps<TComponentType, TComponent>): UseWizardComponents<TComponentType, TComponent> => {
  const componentsPropsLock = useRef<TComponent[]>([]);
  const componentFnsLock = useRef<WizardComponentAdapterFn<TComponentType>[]>([]);

  const componentsProps = useMemo(() => {
    if (currentStepComponent == null) return [];
    if (presentationLock.current) return componentsPropsLock.current;

    const currentStepComponents = wrap(currentStepComponent);
    return isEqual(currentStepComponents, componentsPropsLock.current)
      ? componentsPropsLock.current
      : currentStepComponents;
  }, [presentationLock, currentStepComponent]);

  componentsPropsLock.current = componentsProps;

  const componentFns = useMemo(() => {
    const components = componentsProps.map(component => componentsMap[component.type]);

    return isEqual(components, componentFnsLock.current) ? componentFnsLock.current : components;
  }, [componentsMap, componentsProps]);

  componentFnsLock.current = componentFns;

  const { isLoading, components: loadedComponents, error } = useWizardAsyncComponents(componentFnsLock.current);

  const filteredComponentsProps = useMemo(
    () => filterConditionalItems(componentsProps, answers),
    [answers, componentsProps],
  );

  const filteredComponents = useMemo(() => {
    return filterComponentsByProps(loadedComponents, filteredComponentsProps, componentsProps);
  }, [loadedComponents, filteredComponentsProps, componentsProps]);

  return {
    isLoading,
    error,
    components: filteredComponents,
    componentsProps: filteredComponentsProps,
  };
};
